import * as React from "react";
import { useNavigate } from "react-router-dom";
import { useDeleteV4ManufacturerByRouteManufacturerIdOpportunityAndBidNumberMutation } from "randmar-api-client";
import { useManufacturer } from "../../contexts/WithManufacturer";
import Path from "../../routes/Path";
import { Button, Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, useToast } from "randmarcomps";
import CenteredLoader from "../util/CenteredLoader";

interface DeleteOpportunityWrapperProps {
  bidNumber: string;
  disabled?: boolean;
  buttonTitle?: string;
  text?: string;
  children: React.ReactNode;
}

const DeleteOpportunityWrapper: React.FC<DeleteOpportunityWrapperProps> = ({
  bidNumber,
  disabled,
  buttonTitle = "Delete opportunity",
  text = "Are you sure?",
  children,
}) => {
  const { applicationId } = useManufacturer();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [open, setOpen] = React.useState(false);
  const [deleteOpportunity, deleteOpportunityState] = useDeleteV4ManufacturerByRouteManufacturerIdOpportunityAndBidNumberMutation();

  const handleDelete = React.useCallback(async () => {
    if (!bidNumber) return;

    try {
      const success = await deleteOpportunity({
        routeManufacturerId: applicationId,
        bidNumber,
      }).unwrap();

      if (success) {
        toast({ variant: "success", title: "Deleted", description: "Opportunity deleted successfully." });
        navigate(Path.opportunities(applicationId));
      } else {
        toast({ variant: "destructive", title: "Error", description: "There was an error deleting the opportunity." });
      }
    } catch (error) {
      console.error("Failed to delete opportunity:", error);
      toast({ variant: "destructive", title: "Error", description: "There was an error deleting the opportunity." });
    }
  }, [applicationId, bidNumber, deleteOpportunity, navigate, toast]);

  return (
    <>
      <div className="contents" onClick={() => !disabled && setOpen(true)}>
        {children}
      </div>
      <Dialog open={open} onOpenChange={(isOpen) => !isOpen && !deleteOpportunityState.isLoading && setOpen(false)}>
        <DialogContent className="sm:max-w-md">
          {deleteOpportunityState.isLoading ? (
            <CenteredLoader text="Deleting…" variant="plain" className="min-h-[260px]" />
          ) : (
            <>
              <DialogHeader>
                <DialogTitle>{buttonTitle}</DialogTitle>
              </DialogHeader>
              <div className="py-4 text-[15px] text-slate-700">{text}</div>
              <DialogFooter className="flex flex-wrap justify-between gap-2">
                <Button type="button" variant="outline" className="rounded-xl" onClick={() => setOpen(false)}>
                  Cancel
                </Button>
                <Button type="button" className="rounded-xl" onClick={handleDelete}>
                  Confirm
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default DeleteOpportunityWrapper;
